import React, { Component } from 'react';
import { bool, number, any, func, shape, string, array, arrayOf } from 'prop-types';
import { Column, Table, defaultTableRowRenderer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import Radium from 'radium';
import Rollbar from 'rollbar';
import * as Scroll from 'react-scroll';

import {
  sidebarSize,
  defaultPaddingLeft,
  topbarSize,
  customerSupportTopbarSize
} from '../../../constants/sizes';
import { TabletBreakpoint } from '../../../constants/deviceSizes';
import Images from '../../../config/assets';

import styles from './styles';

const scroll = Scroll.animateScroll;

class UTTable extends Component {
  constructor(props) {
    super(props);
    this.state = { ...this.getTableSize(), activeRow: -1 };
    window.addEventListener('resize', this.updateTableSize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateTableSize);
  }

  getTableSize = () => ({
    width: window.innerWidth - sidebarSize * (window.innerWidth > TabletBreakpoint),
    height: window.innerHeight - topbarSize - customerSupportTopbarSize
  });

  updateTableSize = () => this.setState({ ...this.getTableSize() });

  rowIsActive = index => this.state.activeRow === index;

  handleRowClick = ({ index, event }) => {
    try {
      const associateClick =
        ((event.target && event.target.id) ||
          (event.target.parentElement && event.target.parentElement.id)) === this.props.clickExceptionId;
      if (!associateClick) {
        if (!this.props.openable) {
          return;
        }
      }
      if (this.state.activeRow === index) {
        this.setState({ activeRow: -1 }, () => this.table && this.table.recomputeRowHeights(index));
      } else {
        // recomputeRowHeights receives a row index to update measurements for indexes greater or equals to
        // that passed by parameter. We need to use min() since we don't know if the active content
        // were below or above the clicked row.
        const indexToUpdate = this.state.activeRow === -1 ? 0 : Math.min(this.state.activeRow, index);
        this.setState(
          { activeRow: index },
          () => this.table && this.table.recomputeRowHeights(indexToUpdate)
        );
      }
      if (index === this.props.rows.length - 1) {
        scroll.scrollToBottom({ duration: 0 });
      }
    } catch (error) {
      Rollbar.error('Table row click error', {
        error,
        rows: this.props.rows,
        index,
        event
      });
    }
  };

  renderRow = params => {
    const columnCount = this.props.columns.length;
    const currentRowIsActive = this.rowIsActive(params.index);
    let chosenStyle = styles.row;
    if (!this.props.withoutOddStyle) {
      chosenStyle = {
        ...chosenStyle,
        ...(params.index % 2 ? styles.oddRow : styles.evenRow)
      };
    } else if (this.props.backgroundColor) {
      chosenStyle.backgroundColor = this.props.backgroundColor;
    }
    if (currentRowIsActive) {
      chosenStyle = {
        ...chosenStyle,
        ...this.props.activeRowStyle
      };
    }

    if (this.props.openable) {
      // An aux column will contain this image node, since virtualized-list manages
      // width for columns
      params.columns[columnCount + 1] = (
        <div style={styles.listArrow}>
          <img
            alt=""
            key={params.rowData.id}
            src={currentRowIsActive ? Images.listArrowUp : Images.listArrowDown}
          />
        </div>
      );
      chosenStyle = {
        ...chosenStyle,
        ...styles.openableRow,
        width: '100%'
      };
    }

    // We need to recalculate where content will be rendered, since
    // all Table rows will have position === absolute.
    // Rows positions are calculated by recomputeRowHeights()
    return (
      <div key={`${this.props.id}-container-${params.key}`}>
        {defaultTableRowRenderer({
          ...params,
          key: `${this.props.id}-${params.key}`,
          style: {
            ...params.style,
            ...chosenStyle
          }
        })}
        {this.props.renderActiveRow
          ? this.props.renderActiveRow(params.rowData, [
              styles.activeRowOpenable,
              {
                visibility: !currentRowIsActive ? 'hidden' : 'visible',
                top: (params.index + 1) * this.props.rowHeight(params.index),
                width: currentRowIsActive ? this.state.width : 0,
                opacity: !currentRowIsActive ? 0 : 1,
                maxHeight: currentRowIsActive ? '100%' : 0
              }
            ])
          : null}
      </div>
    );
  };

  render() {
    return (
      <div style={styles.container}>
        <Table
          height={this.props.height || this.state.height - this.props.heightToSubstract}
          width={this.props.width || this.state.width}
          headerHeight={this.props.headerHeight}
          rowGetter={({ index }) => this.props.rows[index]}
          rowCount={this.props.rows.length}
          rowRenderer={this.renderRow}
          rowHeight={({ index }) => this.props.rowHeight(index, this.state.activeRow)}
          onRowClick={this.handleRowClick}
          onRowsRendered={this.props.onRowsRendered}
          headerStyle={this.props.alignLeft ? { ...styles.header, ...styles.headerLeft } : styles.header}
          rowStyle={styles.headerRow}
          gridStyle={{
            ...(!this.props.unbordered && styles.bordered),
            ...(!this.props.scrolleable && styles.gridWithoutScroll),
            ...(this.props.scrolleable && this.props.gridStyle)
          }}
          ref={table => (this.table = table)}
          id={this.props.id}
        >
          <Column label=" " key="#" dataKey="#" width={defaultPaddingLeft} style={styles.column} />
          {this.props.columns.map(column => (
            <Column
              label={column.name}
              key={column.key}
              dataKey={column.key}
              width={
                (column.width && column.width * this.state.width) ||
                this.state.width / this.props.columns.length - defaultPaddingLeft
              }
              style={this.props.alignLeft ? { ...styles.column, ...styles.columnLeft } : styles.column}
              cellRenderer={column.cellRenderer}
            />
          ))}
          {this.props.openable && (
            <Column label=" " key="$" dataKey="$" width={this.state.width / 30} style={styles.column} />
          )}
        </Table>
      </div>
    );
  }
}

UTTable.defaultProps = {
  headerHeight: 50,
  rowHeight: () => 50,
  scrolleable: false,
  heightToSubstract: 0
};

UTTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  rows: array,
  columns: arrayOf(
    shape({
      key: string.isRequired,
      name: string.isRequired,
      width: number
    })
  ).isRequired,
  openable: bool,
  renderActiveRow: func,
  onRowsRendered: func,
  headerHeight: number,
  rowHeight: func,
  height: number,
  heightToSubstract: number,
  // eslint-disable-next-line react/forbid-prop-types
  activeRowStyle: any,
  id: string.isRequired,
  scrolleable: bool,
  withoutOddStyle: bool,
  unbordered: bool,
  backgroundColor: string,
  clickExceptionId: number,
  width: number,
  alignLeft: bool,
  // eslint-disable-next-line react/forbid-prop-types
  gridStyle: any
};

export default Radium(UTTable);
